import { Injectable, inject } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { Observable } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { TenantService } from '../tenant-id/tenant.service';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class WebSocketService {
  private authService = inject(AuthService);
  private tenantService = inject(TenantService);

  private socket: Socket | null = null;

  /**
   * Connect to WebSocket server.
   */
  connect(): void {
    const token = this.authService.getAuthToken();
    const tenantId = this.tenantService.getTenantId();

    if (!token || !tenantId) {
      console.error('Cannot connect to WebSocket: Missing token or tenantId');
      return;
    }

    // If socket already exists, just update the auth token for reconnections
    if (this.socket) {
      this.socket.auth = { token };
      if (!this.socket.connected) {
        this.socket.connect();
      }
      return;
    }

    this.socket = io(environment.urls.websocketApi.replace('/websocket', ''), {
      path: '/websocket/socket.io/',
      auth: { token },
      query: { tenantId },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity,
      transports: ['websocket']
    });

    this.setupSocketListeners();
  }

  /**
   * Setup socket event listeners
   */
  private setupSocketListeners(): void {
    if (!this.socket) return;

    this.socket.on('connect_error', error => {
      console.error('WebSocket connection error:', error.message, error);
    });
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  /**
   * Listen for specific event
   * @param event Event name to listen for
   * @returns Observable of event data
   */
  on<T = any>(event: string): Observable<T> {
    return new Observable(observer => {
      if (!this.socket) {
        console.warn(`Cannot listen to '${event}': Not connected`);
        return;
      }

      const handler = (data: T) => {
        observer.next(data);
      };

      if (this.socket.connected) {
        this.socket.on(event, handler);
      } else {
        this.socket.once('connect', () => {
          this.socket?.on(event, handler);
        });
      }

      return () => {
        this.socket?.off(event, handler);
      };
    });
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.socket?.connected || false;
  }
}
